import { Link } from "react-router-dom";
import { ArrowRight, Music, Headphones, Disc3, Cross, ClipboardList, Rocket, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Layout from "@/components/layout/Layout";
import { Helmet } from "react-helmet-async";
import { FeaturedArtist } from "@/components/sections/FeaturedArtist";
import { FeaturedRelease } from "@/components/sections/FeaturedRelease";

const services = [
  { icon: Music, title: "Artist Development", description: "Comprehensive development programs for emerging artists across every genre." },
  { icon: Headphones, title: "Professional Mastering", description: "Industry-standard mastering to bring your sound to its fullest potential." },
  { icon: Disc3, title: "Release Management", description: "End-to-end release coordination from recording to distribution." },
  { icon: Cross, title: "Theological Integrity", description: "Grounded in Scripture. Every artist, every release, every decision." },
];

const steps = [
  { icon: ClipboardList, number: "01", title: "Apply", description: "Submit your application with your music, vision, and ministry statement." },
  { icon: Sparkles, number: "02", title: "Develop", description: "Work with our team on mastering, branding, and release strategy." },
  { icon: Rocket, number: "03", title: "Release", description: "Launch your music to all major platforms with full label support." },
];

const orgSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Nail Point Records",
  "url": "https://nailpointrecords.com",
  "description": "A creative house for Christian artists who take their craft and their calling seriously.",
  "foundingDate": "2024",
  "sameAs": [],
};

const Index = () => {
  return (
    <Layout>
      <Helmet>
        <title>Nail Point Records | Christian Artist Development & Releases</title>
        <meta name="description" content="A creative house for Christian artists who take their craft and their calling seriously." />
        <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative bg-card text-foreground py-28 md:py-40 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-card via-card to-background" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight tracking-tight">
              Shaping Sound With Weight and Purpose
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
              A creative house for Christian artists who take their craft and their calling seriously.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild className="bg-accent text-accent-foreground hover:bg-accent/90 text-lg px-8">
                <Link to="/apply">Apply as Artist <ArrowRight className="ml-2 h-5 w-5" /></Link>
              </Button>
              <Button size="lg" variant="outline" asChild className="border-border text-foreground hover:bg-accent/10 text-lg px-8">
                <Link to="/artists">Meet Our Artists</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">What We Do</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">A full infrastructure for Christian artists — from development to distribution.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service) => (
              <Card key={service.title} className="group hover:border-accent/40 transition-all duration-300">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-accent group-hover:text-accent-foreground transition-colors">
                    <service.icon className="h-6 w-6 text-accent group-hover:text-accent-foreground" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{service.title}</h3>
                  <p className="text-muted-foreground text-sm">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-20 bg-card/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">How We Work</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">From application to release day — a simple, artist-first process.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {steps.map((step, i) => (
              <div key={step.number} className="relative text-center">
                <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                  <step.icon className="h-7 w-7 text-accent" />
                </div>
                <span className="text-xs font-bold text-accent/70 tracking-widest">{step.number}</span>
                <h3 className="text-xl font-bold text-foreground mt-1 mb-2">{step.title}</h3>
                <p className="text-muted-foreground text-sm">{step.description}</p>
                {i < steps.length - 1 && (
                  <ArrowRight className="hidden md:block absolute top-8 -right-4 h-5 w-5 text-muted-foreground/40" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <FeaturedArtist />
      <FeaturedRelease />

      {/* CTA */}
      <section className="py-20 bg-card">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Built on Conviction. Refined Through Craft.</h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-xl mx-auto">We're looking for artists who are serious about their sound.</p>
          <Button size="lg" asChild className="bg-accent text-accent-foreground hover:bg-accent/90">
            <Link to="/apply">Start Your Application</Link>
          </Button>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
